import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { adminTenantRowSchema, tenantPlanOptionSchema } from './tenants.screen.js'
import { adminPaginationSchema, systemUserRowSchema } from './users.screen.js'

/*
 * One organisation of the super admin panel (`admin.tenants.detail`, #4419): its record (`tenant`), the data of the tab
 * that is open (`tab`) and the plan catalogue (`plans`). The `tab` section carries ONLY the requested tab, as a union
 * discriminated by `tab`: whoever opens «General» does not pay for the audit trail, and the four tabs without a list
 * («General», «Suscripción», «Configuración», «Uso») are painted with `tenant` and `plans`. An organisation that does
 * not exist is the 404 of the whole screen, not a failed section. No row carries a credential or the values of an
 * audit change: an audit row has seven keys, a plan has no `tenantCount`, `limits` or `pricing`.
 */

/** Rows per page of the lists of the detail («Usuarios», «Registro de auditoría»). */
export const TENANT_DETAIL_DEFAULT_LIMIT = 20

export const tenantDetailTabSchema = z.enum([
  'general',
  'subscription',
  'users',
  'configuration',
  'usage',
  'audit',
])
export type TenantDetailTab = z.infer<typeof tenantDetailTabSchema>

/** The record of the detail: the row of the list plus contact, settings and usage (`AdminTenantDetailResponse`). */
export const adminTenantDetailSchema = adminTenantRowSchema.extend({
  contactEmail: z.string().optional(),
  contactPhone: z.string().optional(),
  updatedAt: z.string(),
  notes: z.string().optional(),
  settings: z
    .strictObject({
      timezone: z.string().optional(),
      language: z.string().optional(),
      currency: z.string().optional(),
      dateFormat: z.string().optional(),
      taxCountry: z.string().optional(),
      valuationMethod: z.string().optional(),
      taxId: z.string().optional(),
      apiAccess: z.boolean().optional(),
      customDomain: z.boolean().optional(),
      defiTracking: z.boolean().optional(),
    })
    .optional(),
  transactionsCount: z.number().optional(),
  dataSourcesCount: z.number().optional(),
  reportsCount: z.number().optional(),
  /** `unavailable` is not 0: the page says «no disponible». Absent: an older server, read as «not available» too. */
  apiRequests: z
    .discriminatedUnion('status', [
      z.strictObject({
        status: z.literal('ok'),
        calls: z.number(),
        errors: z.number(),
        windowDays: z.number(),
      }),
      z.strictObject({ status: z.literal('unavailable'), windowDays: z.number().optional() }),
    ])
    .optional(),
  limits: z
    .strictObject({
      users: z.number(),
      transactions: z.number(),
      storageBytes: z.number(),
      apiCalls: z.number(),
    })
    .optional(),
})

export const planFeatureSchema = z.strictObject({
  key: z.string(),
  label: z.string(),
  enabled: z.boolean(),
})

export const tenantDetailPlanSchema = tenantPlanOptionSchema.extend({
  features: z.array(planFeatureSchema),
})

/** An audit row cut to what the tab prints: no `changes` (old and new values) and no `metadata`. */
export const tenantAuditRowSchema = z.strictObject({
  id: z.string(),
  timestamp: z.string(),
  action: z.string(),
  resourceType: z.string(),
  resourceId: z.string().nullable(),
  resourceName: z.string().nullable(),
  actorEmail: z.string(),
})

/** The four tabs without a list are painted with `tenant` and `plans`: they carry only their name. */
export const tenantDetailTabDataSchema = z.discriminatedUnion('tab', [
  z.strictObject({ tab: z.literal('general') }),
  z.strictObject({ tab: z.literal('subscription') }),
  z.strictObject({ tab: z.literal('configuration') }),
  z.strictObject({ tab: z.literal('usage') }),
  z.strictObject({
    tab: z.literal('users'),
    items: z.array(systemUserRowSchema),
    pagination: adminPaginationSchema,
  }),
  z.strictObject({
    tab: z.literal('audit'),
    items: z.array(tenantAuditRowSchema),
    pagination: adminPaginationSchema,
  }),
])

export const adminTenantDetailQuerySchema = z.strictObject({
  id: z.guid(),
  tab: tenantDetailTabSchema.default('general'),
  page: z.coerce.number<number>().int().min(1).default(1),
  limit: z.coerce.number<number>().int().min(1).max(50).default(TENANT_DETAIL_DEFAULT_LIMIT),
})

// Sections run in series, in this order: `tenant` first, so a missing organisation is a 404 before anything else is read
export const adminTenantDetailScreen = defineScreen({
  id: 'admin.tenants.detail',
  path: '/api/screens/admin/tenants/detail',
  access: 'super_admin',
  query: adminTenantDetailQuerySchema,
  response: z.strictObject({
    tenant: adminTenantDetailSchema,
    tab: screenSection(tenantDetailTabDataSchema),
    plans: screenSection(z.array(tenantDetailPlanSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.tenants', 'admin.users', 'admin.audit'],
  budget: { requests: 1, statements: 14, bytes: 60_000, p95Ms: 250 },
  summary:
    'Organizations › one organization: its record, the data of the open tab and the plan catalogue',
})

export type TenantDetailTabData = z.infer<typeof tenantDetailTabDataSchema>
export type TenantDetailPlan = z.infer<typeof tenantDetailPlanSchema>
export type TenantAuditRow = z.infer<typeof tenantAuditRowSchema>
export type TenantDetailUserRow = Extract<TenantDetailTabData, { tab: 'users' }>['items'][number]
export type AdminTenantDetailQuery = ScreenQuery<typeof adminTenantDetailScreen>
export type AdminTenantDetailData = ScreenData<typeof adminTenantDetailScreen>
